import type { ComponentType, FC } from 'react'
import type { SvgIconProps, SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip, useTheme } from '@mui/material'
import { ArrowBack, Reply as ReplyIcon } from '@mui/icons-material'
import type { ConversationTurn } from '@/index'
import type { FormattedUserActivity } from '@/features/events/shared'
import {
  EventType,
  getIconRotation,
  isBlockingConversationEventType,
  resolveEventTypeColorFor,
  resolveEventTypeIconFor,
  shouldRotateIcon,
} from '@/features/events/shared'
import { standardTooltipProps } from '@/common/components'

interface EventStatusIconProps {
  readonly conversationTurn: ConversationTurn | undefined
  readonly isProcessing: boolean
  readonly isPaused: boolean
  readonly isInteractable?: boolean
  readonly onReply: () => void
  readonly onViewMostRecent?: () => void
  readonly userActivity?: FormattedUserActivity | null
}

export const ConversationEventStatusIcon: FC<EventStatusIconProps> = ({
  conversationTurn,
  isProcessing,
  isPaused,
  isInteractable = true,
  onReply,
  onViewMostRecent,
  userActivity,
}) => {
  const theme = useTheme()

  const events = conversationTurn?.events ?? []
  const userCommands = conversationTurn?.userCommands ?? []
  const latestEvent = events.at(-1)
  const isAcknowledged = userActivity?.isBlockingResponse && userActivity?.isAcknowledged

  if (events.length === 0 && userCommands.length > 0) {
    return renderProcessingIcon(theme, isPaused)
  }

  if (!latestEvent) {
    return null
  }

  if (latestEvent.eventType === EventType.AGENT_REPLY && !isAcknowledged) {
    if (isInteractable) {
      return renderReplyButton(theme, onReply)
    }
    return null
  }

  const eventType =
    isAcknowledged && userActivity?.pendingAcknowledgement
      ? userActivity.pendingAcknowledgement.eventType
      : latestEvent.eventType

  const Icon = resolveEventTypeIconFor(eventType)
  if (!Icon) {
    return null
  }

  const isCompletedToolUsage = latestEvent.eventType === EventType.FILE_MODIFICATION
  const shouldPulse = isProcessing && !isPaused && !isCompletedToolUsage && !isAcknowledged
  const color = isAcknowledged
    ? theme.palette.success.main
    : resolveEventTypeColorFor(eventType, theme)

  return renderEventIcon({
    eventType,
    Icon,
    color,
    shouldPulse,
    isAcknowledged: !!isAcknowledged,
    onViewMostRecent,
  })
}

interface RenderEventIconOptions {
  readonly eventType: EventType
  readonly Icon: ComponentType<SvgIconProps>
  readonly color: string
  readonly shouldPulse: boolean
  readonly isAcknowledged: boolean
  readonly onViewMostRecent?: (() => void) | undefined
}

function renderProcessingIcon(theme: Theme, isPaused: boolean) {
  const arrowSx = isPaused
    ? staticIconSx(theme.palette.success.main)
    : pulsingIconSx(theme.palette.success.main)
  const tooltip = isPaused ? 'Paused' : 'Processing your prompt...'

  return (
    <Tooltip {...standardTooltipProps} title={tooltip}>
      <Box sx={iconContainerSx(false, 0)}>
        <ArrowBack sx={arrowSx} />
      </Box>
    </Tooltip>
  )
}

function renderReplyButton(theme: Theme, onReply: () => void) {
  return (
    <Tooltip {...standardTooltipProps} title="Reply to Agent">
      <IconButton onClick={onReply} sx={replyButtonSx}>
        <ReplyIcon sx={staticIconSx(theme.palette.success.main)} />
      </IconButton>
    </Tooltip>
  )
}

function renderEventIcon(options: RenderEventIconOptions) {
  const { eventType, Icon, color, shouldPulse, isAcknowledged, onViewMostRecent } = options
  const rotate = shouldRotateIcon(eventType)
  const rotationAngle = getIconRotation(eventType)
  const tooltip = isAcknowledged ? 'Request acknowledged' : getTooltipText(eventType, shouldPulse)
  const iconSx = shouldPulse ? pulsingIconSx(color) : staticIconSx(color)
  const isClickable = isBlockingConversationEventType(eventType) && onViewMostRecent

  if (isClickable) {
    return (
      <Tooltip {...standardTooltipProps} title={`${tooltip} - Click to view`}>
        <IconButton onClick={onViewMostRecent} sx={blockingEventButtonSx}>
          <Box sx={iconContainerSx(rotate, rotationAngle)}>
            <Icon sx={iconSx} />
          </Box>
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Tooltip {...standardTooltipProps} title={tooltip}>
      <Box sx={iconContainerSx(rotate, rotationAngle)}>
        <Icon sx={iconSx} />
      </Box>
    </Tooltip>
  )
}

function getTooltipText(eventType: EventType, isProcessing: boolean): string {
  if (eventType === EventType.ERRORED_EVENT) {
    return 'Error occurred'
  }

  if (isProcessing) {
    return 'Claude is working...'
  }

  switch (eventType) {
    case EventType.TOOL_PERMISSION_REQUEST:
      return 'Awaiting tool permission'
    case EventType.PLAN_APPROVAL_REQUEST:
      return 'Awaiting plan approval'
    case EventType.AGENT_QUESTIONS:
      return 'Awaiting your answers'
    case EventType.AGENT_REPLY:
      return 'Claude replied'
    default:
      return 'Idle'
  }
}

const iconContainerSx = (rotate: boolean, rotationAngle: number): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: rotate ? `rotate(${rotationAngle}deg)` : 'none',
})

const pulsingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1.75rem',
  color: color,
  animation: 'iconPulse 2s ease-in-out infinite',
  '@keyframes iconPulse': {
    '0%': { opacity: 0.6, transform: 'scale(1)' },
    '50%': { opacity: 1, transform: 'scale(1.15)' },
    '100%': { opacity: 0.6, transform: 'scale(1)' },
  },
})

const staticIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1.75rem',
  color: color,
  opacity: 0.7,
})

const replyButtonSx: SxProps<Theme> = {
  padding: '4px',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
}

const blockingEventButtonSx: SxProps<Theme> = {
  padding: '4px',
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
}
